import cs from "classnames";
import React from "react";

import "./ButtonGroup.css";

interface ButtonGroupProps {
	variant?: ButtonGroupVariant;
	disabled?: boolean;
	children: React.ReactElement<{ disabled?: boolean }>[];
}

export enum ButtonGroupVariant {
	Round = "round",
	Square = "square"
}

export const ButtonGroup: React.FunctionComponent<ButtonGroupProps> = ({
	variant = ButtonGroupVariant.Round,
	disabled,
	children
}) => (
	<div
		className={cs("button-group", `button-group--${variant}`, {
			"button-group--disabled": disabled
		})}
	>
		{children.map((child, key) =>
			React.cloneElement(child, { key, disabled })
		)}
	</div>
);

interface ButtonGroupItemProps {
	disabled?: boolean;
	title?: string;
	checked: boolean;
	value: string | undefined;
	onChangeValue(value: string | undefined): unknown;
}

export const ButtonGroupItem: React.FunctionComponent<
	React.PropsWithChildren<ButtonGroupItemProps>
> = ({ disabled, title, checked, value, onChangeValue, children }) => {
	const [{ isFocused, isMouseDown }, setState] = React.useState({
		isFocused: false,
		isMouseDown: false
	});

	const onFocus = React.useCallback(() => {
		setState(state => ({ ...state, isFocused: true }));
	}, []);

	const onBlur = React.useCallback(() => {
		setState(state => ({ ...state, isFocused: false }));
	}, []);

	const onMouseDown = React.useCallback(() => {
		setState(state => ({ ...state, isMouseDown: true }));
	}, []);

	const onMouseUp = React.useCallback(() => {
		setState(state => ({ ...state, isFocused: false, isMouseDown: false }));
	}, []);

	const onClick = React.useCallback(() => {
		onChangeValue(checked ? undefined : value);
	}, [onChangeValue, checked, value]);

	const onKeyDown = React.useCallback(
		(event: React.KeyboardEvent) => {
			if (event.key === " ") {
				setState(state => ({ ...state, isFocused: true }));
				onChangeValue(value);
			}
		},
		[onChangeValue, value]
	);

	return (
		<div
			role="radio"
			aria-checked={checked}
			title={title}
			className={cs("button-group-item", {
				"button-group-item--disabled": disabled,
				"button-group-item--checked": checked,
				"button-group-item--focused": isFocused && !isMouseDown
			})}
			tabIndex={disabled ? -1 : 0}
			onFocus={onFocus}
			onBlur={onBlur}
			onMouseDown={onMouseDown}
			onMouseUp={onMouseUp}
			onClick={onClick}
			onKeyDown={onKeyDown}
		>
			{children}
		</div>
	);
};
